import * as CJMapOp from "./Operation_gen";
import {CJMapManager} from "./CJmapManager";
import {Operation} from "./Operation";
import {if_null} from "../utils/functions";
import {ContentKeyPair} from "./Keys";
import {debug_log} from "../utils/debug";
import {network_error} from "../utils/browser";

export class ManagerHistory {

    private undo_log: Operation[][];
    private redo_log: Operation[][];
    private last_manager: CJMapManager;
    private operation_sender: (op: Operation) => Promise<any> = async () => {
    };

    constructor() {
        this.undo_log = [];
        this.redo_log = [];
        this.last_manager = CJMapManager.createNew();
    }

    get LastManager(): CJMapManager {
        return this.last_manager;
    }

    get HasUndoOperation(): boolean {
        return 0 < this.undo_log.length;
    }

    get HasRedoOperation(): boolean {
        return 0 < this.redo_log.length;
    }

    undo(mgr: CJMapManager): CJMapManager {
        debug_log("undo_log", Array.from(this.undo_log));
        const undo_ops = this.undo_log.pop();
        if (undo_ops === undefined) {
            throw new Error("noop");
        }
        debug_log("undo_ops", Array.from(undo_ops));

        const [reverse_ops, after_mgr] = this._push(mgr, undo_ops);
        this.redo_log.push(reverse_ops);

        return after_mgr;
    }

    redo(mgr: CJMapManager): CJMapManager {
        debug_log("redo_log", Array.from(this.redo_log));
        const redo_ops = this.redo_log.pop();
        if (redo_ops === undefined) {
            throw new Error("noop");
        }
        debug_log("redo_ops", Array.from(redo_ops));

        const [reverse_ops, after_mgr] = this._push(mgr, redo_ops);
        this.undo_log.push(reverse_ops);
        return after_mgr;
    }

    push(mgr: CJMapManager, op: Operation): CJMapManager {
        return this.multiPush(mgr, [op]);
    }

    multiPush(mgr: CJMapManager, ops: Operation[]): CJMapManager {
        const [reverse_ops, after_mgr] = this._push(mgr, ops);
        this.undo_log.push(reverse_ops);
        this.redo_log = [];
        return after_mgr;
    }

    clear() {
        this.undo_log = [];
        this.redo_log = [];
        this.last_manager = CJMapManager.createNew();
    }

    private _push(mgr: CJMapManager, ops: Operation[]): [Operation[], CJMapManager] {
        // 変更適応前の移動警告等に利用する場合は、この promise を利用する
        const promise = this._sendOperations(ops);

        let _mgr = mgr;
        let reverse_ops: Operation[] = [];
        for (const op of ops) {
            [ops, _mgr] = this.run(_mgr, op);
            reverse_ops = reverse_ops.concat(ops.reverse());
        }
        // run で構築した reverse_ops は、逆順にしてから追加した後、最後にまた逆順にすることで、構築した時の順で実行される
        // 例えば remove によって構築される reverse_ops は [append, set] なので、戻り値は [append, set]
        // これを undo で実行すると [append -> [remove], set -> [set]] となり、 戻り値は [remove, set]
        // さらに redo で実行すると [remove -> [append, set], set -> [set]] となり、戻り値は [set, set, append]
        // undo, redo を繰り返すと 実行される ops が増殖するが、通常は行わない操作なので、許容する
        // (問題あるようなら、 一定数以上の op を含む場合の undo_log あるいは redo_log への追加を禁止すればいい)
        return [reverse_ops.reverse(), _mgr];
    }

    private async _sendOperations(ops: Operation[]) {
        for (const op of ops) {
            debug_log("send op", op);

            try {
                await this.operation_sender(op);
            } catch (e) {
                debug_log(e);
                network_error();
            }
        }
    }

    run(mgr: CJMapManager, op: Operation): [Operation[], CJMapManager] {
        const [ops, new_mgr] = this._run(mgr, op);
        this.last_manager = new_mgr;
        return [ops, new_mgr];
    }

    private _run(mgr: CJMapManager, op: Operation): [Operation[], CJMapManager] {

        if (CJMapOp.isTypeUpdateJourneyMap(op)) {
            // 履歴を初期化
            return [[], mgr.setNewJourneyMap(op.JourneyMap)];
        } else if (CJMapOp.isTypeUpdateTitle(op)) {
            return [
                [new CJMapOp.OpUpdateTitle({title: mgr.Title})],
                mgr.setTitle(op.Title),
            ];
        } else if (CJMapOp.isTypeUpdateBackground(op)) {
            return [
                [new CJMapOp.OpUpdateBackground({background_key: mgr.Background})],
                mgr.setBackgroundKey(op.BackgroundKey),
            ]
        } else if (CJMapOp.isTypeUpdateColorScheme(op)) {
            return [
                [new CJMapOp.OpUpdateColorScheme({color_scheme_key: mgr.ColorScheme.Key})],
                mgr.setColorSchemeKey(op.ColorSchemeKey),
            ];

            // ==========================================================================================================
            // Funnel
            // ==========================================================================================================
        } else if (CJMapOp.isTypeAppendFunnel(op)) {
            return [
                [new CJMapOp.OpRemoveFunnel({funnel_key: op.Funnel.Key})],
                mgr.appendFunnel(op.Funnel, op.Position),
            ];
        } else if (CJMapOp.isTypeRemoveFunnel(op)) {
            const current_funnel_header = mgr.FunnelHeaders.findByKey(op.FunnelKey);
            return [
                [
                    new CJMapOp.OpAppendFunnel({
                        funnel: current_funnel_header,
                        position: mgr.FunnelHeaders.findIndexByKey(current_funnel_header.Key)
                    }),
                    ...mgr.RoleHeaders.Keys.map(role_key =>
                        new CJMapOp.OpSetRoleCellName({
                            funnel_keys: [current_funnel_header.Key],
                            role_key: role_key,
                            name: mgr.getRoleCellData(current_funnel_header.Key, role_key)
                        })
                    ),
                    ...mgr.ContentHeaders.map(content_header =>
                        new CJMapOp.OpUpdateCellContent({
                            cell_key: new ContentKeyPair(content_header.Key, current_funnel_header.Key),
                            cell_content: mgr.getContentCellData(current_funnel_header.Key, content_header.Key),
                            width: current_funnel_header.Size,
                            height: content_header.Size,
                        })
                    ),
                ],
                mgr.removeFunnel(op.FunnelKey),
            ];
        } else if (CJMapOp.isTypeMoveFunnel(op)) {
            return [
                [
                    new CJMapOp.OpMoveFunnel({
                        funnel_key: op.FunnelKey,
                        position: mgr.FunnelHeaders.findIndexByKey(op.FunnelKey)
                    })
                ],
                mgr.moveFunnel(op.FunnelKey, op.Position),
            ];
        } else if (CJMapOp.isTypeSetFunnelName(op)) {
            return [
                [
                    new CJMapOp.OpSetFunnelName({
                        funnel_key: op.FunnelKey,
                        name: mgr.FunnelHeaders.findByKey(op.FunnelKey).Name
                    })
                ],
                mgr.changeFunnelName(op.FunnelKey, op.Name),
            ];
        } else if (CJMapOp.isTypeSetFunnelIsHidden(op)) {
            return [
                [
                    new CJMapOp.OpSetFunnelIsHidden({
                        funnel_keys: op.FunnelKeys,
                        is_hidden: !op.IsHidden
                    })
                ],
                (op.IsHidden)
                    ? mgr.hideFunnels(op.FunnelKeys)
                    : mgr.showFunnels(op.FunnelKeys),
            ];
        } else if (CJMapOp.isTypeSetFunnelGroupName(op)) {

            return [
                op.FunnelKeys.map(funnel_key =>
                    new CJMapOp.OpSetFunnelGroupName({
                        funnel_keys: [funnel_key],
                        name: mgr.FunnelHeaders.findByKey(funnel_key).Group,
                    })
                ),
                mgr.changeFunnelGroup(op.FunnelKeys, op.Name),
            ];

            // ==========================================================================================================
            // Content
            // ==========================================================================================================
        } else if (CJMapOp.isTypeAppendContent(op)) {
            return [
                [new CJMapOp.OpRemoveContent({content_key: op.ContentHeader.Key})],
                mgr.appendContent(op.ContentHeader, op.Position),
            ];
        } else if (CJMapOp.isTypeRemoveContent(op)) {

            const current_content_header = mgr.ContentHeaders.findByKey(op.ContentKey);
            return [
                [
                    new CJMapOp.OpAppendContent({
                        content_header: current_content_header,
                        position: mgr.ContentHeaders.findIndexByKey(current_content_header.Key)
                    }),
                    ...mgr.FunnelHeaders.map(funnel_header =>
                        new CJMapOp.OpUpdateCellContent({
                            cell_key: new ContentKeyPair(op.ContentKey, funnel_header.Key),
                            cell_content: mgr.getContentCellData(funnel_header.Key, current_content_header.Key),
                            width: funnel_header.Size,
                            height: current_content_header.Size,
                        })
                    ),
                ],
                mgr.removeContent(op.ContentKey),
            ];
        } else if (CJMapOp.isTypeMoveContent(op)) {
            return [
                [
                    new CJMapOp.OpMoveContent({
                        content_key: op.ContentKey,
                        position: mgr.ContentHeaders.findIndexByKey(op.ContentKey),
                    })
                ],
                mgr.moveContentLine(op.ContentKey, op.Position),
            ];
        } else if (CJMapOp.isTypeSetContentName(op)) {
            return [
                [
                    new CJMapOp.OpSetContentName(
                        {
                            content_key: op.ContentKey,
                            name: mgr.ContentHeaders.findByKey(op.ContentKey).Name,
                        }
                    )
                ],
                mgr.changeContentHeaderName(op.ContentKey, op.Name),
            ];
        } else if (CJMapOp.isTypeSetContentIsHidden(op)) {
            return [
                [new CJMapOp.OpSetContentIsHidden({
                    content_keys: op.ContentKeys,
                    is_hidden: !op.IsHidden
                })],
                (op.IsHidden)
                    ? mgr.hideLine(op.ContentKeys)
                    : mgr.showLine(op.ContentKeys),
            ];
            // ==========================================================================================================
            // Cell
            // ==========================================================================================================
        } else if (CJMapOp.isTypeUpdateCellContent(op)) {

            return [
                [
                    new CJMapOp.OpUpdateCellContent({
                        cell_key: op.CellKey,
                        cell_content: mgr.getContentCellData(op.CellKey.FunnelKey, op.CellKey.LineKey),
                        width: mgr.FunnelHeaders.findByKey(op.CellKey.FunnelKey).Size,
                        height: mgr.ContentHeaders.findByKey(op.CellKey.LineKey).Size,
                    })
                ],
                mgr.setData(op.CellKey, op.CellContent, op.Width, op.Height),
            ];
        } else if (CJMapOp.isTypeResizeCell(op)) {

            return [
                [
                    new CJMapOp.OpResizeCell({
                        cell_key: op.CellKey,
                        width: mgr.FunnelHeaders.findByKey(op.CellKey.FunnelKey).Size,
                        height: mgr.ContentHeaders.findByKey(op.CellKey.LineKey).Size,
                    })
                ],
                if_null(mgr.resizeCell(op.CellKey, op.Width, op.Height), mgr),
            ]

            // ==========================================================================================================
            // Role
            // ==========================================================================================================
        } else if (CJMapOp.isTypeAppendRole(op)) {
            return [
                [new CJMapOp.OpRemoveRole({role_key: op.RoleHeader.Key})],
                mgr.appendRole(op.RoleHeader, op.Position),
            ];
        } else if (CJMapOp.isTypeRemoveRole(op)) {
            return [
                [
                    new CJMapOp.OpAppendRole({
                        role_header: mgr.RoleHeaders.findByKey(op.RoleKey),
                        position: mgr.RoleHeaders.findIndexByKey(op.RoleKey)
                    }),
                    ...mgr.FunnelHeaders.Keys.map(funnel_key =>
                        new CJMapOp.OpSetRoleCellName({
                            funnel_keys: [funnel_key],
                            role_key: op.RoleKey,
                            name: mgr.getRoleCellData(funnel_key, op.RoleKey)
                        })
                    )
                ],

                mgr.removeRole(op.RoleKey),
            ];
        } else if (CJMapOp.isTypeMoveRole(op)) {
            return [
                [
                    new CJMapOp.OpMoveRole({role_key: op.RoleKey, position: mgr.RoleHeaders.findIndexByKey(op.RoleKey)})
                ],
                mgr.moveRoleLine(op.RoleKey, op.Position),
            ];

        } else if (CJMapOp.isTypeSetRoleName(op)) {
            return [
                [
                    new CJMapOp.OpSetRoleName(
                        {
                            role_key: op.RoleKey,
                            name: mgr.RoleHeaders.findByKey(op.RoleKey).Name,
                        }
                    )
                ],
                mgr.changeRoleHeaderName(op.RoleKey, op.Name),
            ];
        } else if (CJMapOp.isTypeSetRoleCellName(op)) {
            return [
                op.FunnelKeys.map(funnel_key =>
                    new CJMapOp.OpSetRoleCellName({
                        funnel_keys: [funnel_key],
                        role_key: op.RoleKey,
                        name: mgr.getRoleCellData(funnel_key, op.RoleKey)
                    })
                ),
                mgr.changeRoleCellName(op.RoleKey, op.FunnelKeys, op.Name),
            ];

            // ==========================================================================================================
            // Persona
            // ==========================================================================================================
        } else if (CJMapOp.isTypeSetShowPersona(op)) {
            return [
                [
                    new CJMapOp.OpSetShowPersona({show: !mgr.Persona.IsShow})
                ],
                mgr.updatePersona(mgr.Persona.withIsShow(op.Show)),
            ]

        } else if (CJMapOp.isTypeSetPersonaImage(op)) {
            return [
                [
                    new CJMapOp.OpSetPersonaImage({persona_image_key: mgr.Persona.ImageKey})
                ],
                mgr.updatePersona(mgr.Persona.withImageKey(op.PersonaImageKey)),
            ]

        } else if (CJMapOp.isTypeSetPersonaTagline(op)) {
            return [
                [
                    new CJMapOp.OpSetPersonaTagline({tagline: mgr.Persona.Tagline})
                ],
                mgr.updatePersona(mgr.Persona.withTagline(op.Tagline)),
            ]
        } else if (CJMapOp.isTypeSetPersonaSpecName(op)) {
            return [
                [
                    new CJMapOp.OpSetPersonaSpecName({
                        index: op.Index,
                        name: mgr.Persona.Specs.getNameByIndex(op.Index)
                    })
                ],
                mgr.updatePersona(mgr.Persona.withSpecs(
                    mgr.Persona.Specs.withSpecName(op.Index, op.Name)
                )),
            ]
        } else if (CJMapOp.isTypeSetPersonaSpecValue(op)) {
            return [
                [
                    new CJMapOp.OpSetPersonaSpecValue({
                        index: op.Index,
                        value: mgr.Persona.Specs.getValueByIndex(op.Index)
                    })
                ],
                mgr.updatePersona(mgr.Persona.withSpecs(
                    mgr.Persona.Specs.withSpecValue(op.Index, op.Value)
                )),
            ]

        } else if (CJMapOp.isTypeSetPersonaDescription(op)) {
            return [
                [
                    new CJMapOp.OpSetPersonaDescription({description: mgr.Persona.Description})
                ],
                mgr.updatePersona(mgr.Persona.withDescription(op.Description)),
            ]
        }


        throw new Error("not reachable: " + op.type);
    }


    setOperationSender(operation_sender: (op: Operation) => Promise<any>) {
        this.operation_sender = operation_sender;
    }

}
